import { Credits } from '@/components';
import { Button } from 'components/shadcn/button';
import { Card, CardContent } from 'components/shadcn/card';
import { X } from 'lucide-react';
import { forwardRef, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMetadata, Direction, Message, Sender } from '../types';
import { UserInput } from '../UserInput';
import { scrollToBottom } from '../utils';
import { cn } from 'utils';
import { MessageCard } from '@/components/MessageCard';

interface Props {
  chatMetadata: ChatMetadata;
  messages: Message[];
  forceHideCredit?: boolean;
  loading: boolean;
  onSend: (text: string, fileList: File[]) => Promise<void>;
  deleteMessage?: (index: number) => void;
  newMessageScrollTarget?: RefObject<HTMLDivElement>;
  className?: string;
}

const Conversation = forwardRef(
  (
    {
      newMessageScrollTarget,
      className,
      loading,
      forceHideCredit,
      messages,
      chatMetadata,
      deleteMessage,
      onSend,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const defaultTextFieldLabel = t('errors.generalError');

    // Direction will be handled with CSS classes rather than theme settings
    const direction = chatMetadata?.direction === Direction.RTL ? 'rtl' : 'ltr';

    const prevCountRef = useRef(messages.length);

    useEffect(() => {
      if (messages.length > prevCountRef.current) {
        scrollToBottom(newMessageScrollTarget);
      }
      prevCountRef.current = messages.length;
    }, [messages.length]);

    const textFieldLabel =
      messages.length === 1 ? chatMetadata?.textFieldLabel ?? defaultTextFieldLabel : '';

    // Helper to determine message alignment class based on sender and direction
    const getMessageAlignmentClass = (message: Message): string => {
      const isRTL = chatMetadata?.direction === Direction.RTL;
      const isMessageFromAssistant = message.sender === Sender.Assistant;

      if (isRTL) {
        return isMessageFromAssistant ? 'self-start' : 'self-end';
      }

      return isMessageFromAssistant ? 'self-end' : 'self-start';
    };

    const lastMassageIndex = messages.length - 1;

    const onSuggestionClick = (suggestion: string) => {
      onSend(suggestion, []);
    };

    return (
      <div className={cn('h-full w-full', className)} dir={direction}>
        <div className="container mx-auto max-w-4xl h-full flex flex-col space-y-4">
          <div className="sticky top-0 left-0 right-0 h-8 bg-linear-to-b from-background to-transparent" />

          <div className="flex flex-col space-y-6 flex-1">
            {/* on mobile we start the msg from the bottom */}
            <div className="flex-1 md:hidden" />

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.sender === Sender.User ? 'flex-row' : 'flex-row-reverse'} items-start gap-2 w-full`}
                onMouseEnter={() => setHoveredIndex?.(index)}
                onMouseLeave={() => setHoveredIndex?.(null)}
              >
                <div className={cn(getMessageAlignmentClass(message), 'max-w-full')}>
                  <MessageCard
                    showSuggestionList={lastMassageIndex === index}
                    onSuggestionClick={onSuggestionClick}
                    message={message}
                    showSources={chatMetadata?.showSources}
                    customUserText={chatMetadata?.customUserText}
                    customUserBackground={chatMetadata?.customUserBackground}
                  />
                </div>
                {deleteMessage && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('common.delete')}
                    onClick={() => deleteMessage(index)}
                    className={cn(
                      'active:scale-[0.96] transition-[opacity,transform] duration-300',
                      hoveredIndex === index ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex flex-row-reverse">
                <Card className="bg-muted border-0 shadow-none">
                  <CardContent className="p-4">
                    <img src="/loading.gif" alt={t('common.loading')} width={50} />
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* on desktop we start the msg from the top */}
          <div className="md:flex-1 hidden" />

          <div className="sticky bottom-0 bg-background py-4 space-y-4">
            <UserInput
              canAcceptMediaMessages={chatMetadata.canAcceptMediaMessages}
              customRingColor={chatMetadata?.customUserBackground}
              dir={direction}
              label={textFieldLabel}
              onSend={onSend}
              isLoading={loading}
            />
            {!forceHideCredit && chatMetadata.showCredit && <Credits />}
          </div>
        </div>
      </div>
    );
  },
);

Conversation.displayName = 'Conversation';

export { Conversation };
